import React, { useEffect, useRef, useState, useCallback } from "react";
import { Camera, X, RefreshCw, Aperture, Loader2 } from "lucide-react";

/**
 * Live in-browser camera capture using the MediaStream API.
 * Works on both desktop webcams and mobile rear/front cameras.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onCapture: (dataUrl: string, mime: string) => void
 *  - initialStream?: MediaStream  // if provided, used immediately (preserves iOS user-gesture)
 *  - initialFacing?: "environment" | "user"
 */
export default function CameraCapture({
    open,
    onClose,
    onCapture,
    initialStream = null,
    initialFacing = "environment",
}) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [facing, setFacing] = useState(initialFacing);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [hasMultiple, setHasMultiple] = useState(false);

    const stopStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    const attachStream = useCallback(async (stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(() => {});
        }
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cams = devices.filter((d) => d.kind === "videoinput");
            setHasMultiple(cams.length > 1);
        } catch {
            /* ignore */
        }
    }, []);

    const startStream = useCallback(
        async (mode) => {
            setStarting(true);
            setError(null);
            try {
                stopStream();
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: mode },
                        width: { ideal: 1920 },
                        height: { ideal: 1440 },
                    },
                    audio: false,
                });
                await attachStream(stream);
            } catch (e) {
                console.error("camera error", e);
                const msg =
                    e?.name === "NotAllowedError"
                        ? "Camera permission denied. Allow access in your browser settings, or use Upload instead."
                        : e?.name === "NotFoundError"
                            ? "No camera found on this device. Use Upload instead."
                            : "Could not start camera. Use Upload instead.";
                setError(msg);
            } finally {
                setStarting(false);
            }
        },
        [attachStream]
    );

    useEffect(() => {
        if (!open) {
            stopStream();
            return;
        }
        if (initialStream) {
            // Use the stream that was acquired inside the click handler (iOS-safe)
            attachStream(initialStream).catch(() => {});
            return () => stopStream();
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            setError(
                "Live camera isn't supported in this browser. Use Upload instead."
            );
            return;
        }
        startStream(facing);
        return () => stopStream();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const switchCamera = async () => {
        const next = facing === "environment" ? "user" : "environment";
        setFacing(next);
        await startStream(next);
    };

    const capture = () => {
        const v = videoRef.current;
        const c = canvasRef.current;
        if (!v || !c || !v.videoWidth) return;
        c.width = v.videoWidth;
        c.height = v.videoHeight;
        const ctx = c.getContext("2d");
        // Mirror front-camera capture so the saved image matches what the user saw
        if (facing === "user") {
            ctx.translate(c.width, 0);
            ctx.scale(-1, 1);
        }
        ctx.drawImage(v, 0, 0, c.width, c.height);
        const dataUrl = c.toDataURL("image/jpeg", 0.92);
        stopStream();
        onCapture(dataUrl, "image/jpeg");
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(11, 26, 18, 0.85)" }}
            data-testid="camera-modal"
        >
            <div className="relative w-full max-w-lg bg-black rounded-3xl overflow-hidden shadow-2xl">
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-20">
                    <span className="px-3 py-1 rounded-full bg-black/40 text-white text-[11px] tracking-[0.2em] uppercase font-semibold">
                        Live · {facing === "environment" ? "Rear" : "Front"}
                    </span>
                    <button
                        onClick={() => {
                            stopStream();
                            onClose();
                        }}
                        aria-label="Close camera"
                        data-testid="camera-close"
                        className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="aspect-[3/4] sm:aspect-[4/3] bg-black relative">
                    <video
                        ref={videoRef}
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                        style={{
                            transform: facing === "user" ? "scaleX(-1)" : "none",
                        }}
                    />
                    {/* Lens brackets overlay */}
                    <div className="absolute inset-6 pointer-events-none">
                        <div className="absolute top-0 left-0 w-7 h-7 border-t-2 border-l-2 border-white/70 rounded-tl-lg" />
                        <div className="absolute top-0 right-0 w-7 h-7 border-t-2 border-r-2 border-white/70 rounded-tr-lg" />
                        <div className="absolute bottom-0 left-0 w-7 h-7 border-b-2 border-l-2 border-white/70 rounded-bl-lg" />
                        <div className="absolute bottom-0 right-0 w-7 h-7 border-b-2 border-r-2 border-white/70 rounded-br-lg" />
                    </div>

                    {(starting || error) && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white/90 px-6 text-center">
                            {starting && !error && (
                                <>
                                    <Loader2 className="w-7 h-7 animate-spin mb-2" />
                                    <p className="text-sm">Starting camera…</p>
                                </>
                            )}
                            {error && (
                                <>
                                    <Camera className="w-8 h-8 mb-3 opacity-70" />
                                    <p className="text-sm max-w-[280px]">{error}</p>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="bg-black p-5 flex items-center justify-between">
                    <button
                        onClick={() => {
                            stopStream();
                            onClose();
                        }}
                        className="text-white/70 hover:text-white text-sm tracking-wide"
                        data-testid="camera-cancel-btn"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={capture}
                        disabled={!!error || starting}
                        data-testid="camera-capture-btn"
                        aria-label="Capture"
                        className="w-16 h-16 rounded-full border-4 border-white/80 hover:border-white bg-white/10 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <Aperture className="w-7 h-7 text-white" />
                    </button>
                    <button
                        onClick={switchCamera}
                        disabled={!hasMultiple || starting}
                        data-testid="camera-switch-btn"
                        aria-label="Switch camera"
                        className="text-white/70 hover:text-white p-2 disabled:opacity-30"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>

                <canvas ref={canvasRef} className="hidden" />
            </div>
        </div>
    );
}
